import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Box,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  getOperationRuns, reportOperation, type ProductionOrderListItem, type OperationRun, type OperationRunStatus,
} from '../../api/productieOrdine'
import type { ProductionOperationType } from '../../api/productieRute'
import { errorMessage } from '../../api/client'

const OPERATION_TYPE_LABEL: Record<ProductionOperationType, string> = {
  AlimentareFolie: 'Alimentare folie', Incalzire: 'Încălzire', Formare: 'Formare', Racire: 'Răcire',
  TaiereStantare: 'Tăiere / Ștanțare', Stivuire: 'Stivuire', ControlVizual: 'Control vizual',
  Ambalare: 'Ambalare', Paletizare: 'Paletizare', Altele: 'Altele',
}
const STATUS_LABEL: Record<OperationRunStatus, string> = { Planificat: 'Planificat', InExecutie: 'În execuție', Finalizat: 'Finalizat' }
const STATUS_COLOR: Record<OperationRunStatus, 'default' | 'warning' | 'success'> = { Planificat: 'default', InExecutie: 'warning', Finalizat: 'success' }

export default function OperationRunsDialog({ target, onClose }: { target: ProductionOrderListItem | null; onClose: () => void }) {
  const open = target !== null
  const [reportTarget, setReportTarget] = useState<OperationRun | null>(null)
  const { data, isLoading } = useQuery({
    queryKey: ['productie', 'ordine', target?.id, 'operatii'], queryFn: () => getOperationRuns(target!.id), enabled: open,
  })

  const handleClose = () => { setReportTarget(null); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Operații — {target?.number}</DialogTitle>
      <DialogContent>
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={24} /></Box>}
        {data && (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell><TableCell>Operație</TableCell><TableCell>Status</TableCell>
                <TableCell align="right">Bun cumulat</TableCell><TableCell align="right">Rebut cumulat</TableCell>
                <TableCell align="right">Disponibil următoarea</TableCell><TableCell>Lot</TableCell><TableCell>Estimat</TableCell><TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{r.sequenceOrder}</TableCell>
                  <TableCell>{r.operationName} <Chip size="small" label={OPERATION_TYPE_LABEL[r.operationType]} variant="outlined" sx={{ ml: 0.5 }} /></TableCell>
                  <TableCell><Chip size="small" label={STATUS_LABEL[r.status]} color={STATUS_COLOR[r.status]} variant="outlined" /></TableCell>
                  <TableCell align="right">{r.cumulativeGoodQuantity.toFixed(3).replace('.', ',')}</TableCell>
                  <TableCell align="right">{r.cumulativeScrapQuantity.toFixed(3).replace('.', ',')}</TableCell>
                  <TableCell align="right">{r.availableForNextOperation != null ? r.availableForNextOperation.toFixed(3).replace('.', ',') : '—'}</TableCell>
                  <TableCell>{r.outputLotNumber ? <code>{r.outputLotNumber}</code> : '—'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.estimatedStartAt && r.estimatedEndAt
                      ? `${new Date(r.estimatedStartAt).toLocaleString('ro-RO')} → ${new Date(r.estimatedEndAt).toLocaleString('ro-RO')}`
                      : '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button size="small" disabled={r.status === 'Finalizat'} onClick={() => setReportTarget(r)}>Raportează</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Închide</Button>
      </DialogActions>
      <ReportOperationDialog target={reportTarget} orderId={target?.id} orderNumber={target?.number} onClose={() => setReportTarget(null)} />
    </Dialog>
  )
}

function ReportOperationDialog({
  target, orderId, orderNumber, onClose,
}: { target: OperationRun | null; orderId: string | undefined; orderNumber: string | undefined; onClose: () => void }) {
  const open = target !== null
  const qc = useQueryClient()
  const [goodQuantity, setGoodQuantity] = useState('')
  const [scrapQuantity, setScrapQuantity] = useState('')
  const [lotNumber, setLotNumber] = useState('')
  const [notes, setNotes] = useState('')

  const mutation = useMutation({
    mutationFn: () => reportOperation(orderId!, target!.id, {
      goodQuantityThousands: Number(goodQuantity) || 0, scrapQuantityThousands: Number(scrapQuantity) || 0,
      lotNumber: lotNumber || null, zoneId: null, locationId: null, notes: notes || null,
    }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['productie', 'ordine', orderId, 'operatii'] })
      void qc.invalidateQueries({ queryKey: ['productie', 'ordine'] })
      setGoodQuantity(''); setScrapQuantity(''); setLotNumber(''); setNotes('')
    },
  })

  const handleClose = () => { mutation.reset(); onClose() }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>Raportează — {orderNumber} / {target?.operationName}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          {target?.availableForNextOperation != null && (
            <Alert severity="info">Disponibil din operația precedentă: {target.availableForNextOperation.toFixed(3).replace('.', ',')} mii buc.</Alert>
          )}
          <Stack direction="row" spacing={2}>
            <TextField type="number" label="Cantitate bună (mii buc)" value={goodQuantity} onChange={(e) => setGoodQuantity(e.target.value)} sx={{ flexGrow: 1 }} />
            <TextField type="number" label="Rebut (mii buc)" value={scrapQuantity} onChange={(e) => setScrapQuantity(e.target.value)} sx={{ flexGrow: 1 }} />
          </Stack>
          <TextField label="Lot (opțional — generat automat dacă lipsește)" value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} fullWidth />
          <TextField label="Note" value={notes} onChange={(e) => setNotes(e.target.value)} fullWidth multiline minRows={2} />
          {mutation.isSuccess && (
            mutation.data?.scrapRateWarning
              ? <Alert severity="warning">Raportat — dar rata de rebut cumulată depășește 5% (FR-OPE-007).</Alert>
              : <Alert severity="success">Raportat.</Alert>
          )}
          {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose}>Închide</Button>
        <Button variant="contained" disabled={mutation.isPending} onClick={() => mutation.mutate()}
          startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
          Raportează
        </Button>
      </DialogActions>
    </Dialog>
  )
}
